import { sanitizeHtml } from '../lib/sanitize';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { Button } from '../components/ui/button';
import { MapPin, ArrowRight, ShieldCheck, Clock, Award, ChevronLeft } from 'lucide-react';

// Local SEO landing page — one per city. Server-rendered content (intro_html
// + body_html stored in DB and editable from the admin) is dropped into a
// magazine-style layout. Includes JSON-LD `EducationalOrganization` +
// breadcrumb for the city.
export const CityLandingPage = () => {
  const { slug } = useParams();
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const p = await api.getCityPagePublic(slug);
        if (!mounted) return;
        setPage(p);
        document.title = p.meta_title || `Formation sellerie à ${p.city_name} — Sellerie Académie`;
        // Live-update the <meta name="description"> tag for SEO crawlers
        const meta = document.querySelector('meta[name="description"]');
        if (meta && p.meta_description) meta.setAttribute('content', p.meta_description);
      } catch {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center">
        <div className="animate-pulse text-[#1F2A4A]/40">Chargement…</div>
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex flex-col items-center justify-center px-6">
        <p className="text-xs font-mono uppercase tracking-[0.35em] text-[#C9B896] mb-3">404</p>
        <h1 className="font-serif italic text-4xl md:text-5xl text-[#1F2A4A] mb-6">Page introuvable.</h1>
        <Link to={L('/')} className="text-sm text-[#1F2A4A] hover:text-[#C9B896] underline underline-offset-4">
          ← Retour à l'accueil
        </Link>
      </div>
    );
  }

  // JSON-LD for Google — EducationalOrganization with city-targeted name.
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: `Sellerie Académie — formation sellier-garnisseur depuis ${page.city_name}`,
    url: typeof window !== 'undefined' ? window.location.href : '',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Istres',
      addressRegion: page.region,
      postalCode: '13800',
      addressCountry: 'FR',
    },
    areaServed: { '@type': 'City', name: page.city_name },
    sameAs: ['https://www.facebook.com/sellerieacademie'],
  };

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="city-landing-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero */}
      <section className="bg-[#1F2A4A] text-white pt-32 pb-12 lg:pt-40 lg:pb-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <Link
            to={L('/')}
            className="inline-flex items-center gap-1.5 text-xs text-[#C9B896]/85 hover:text-white transition-colors mb-6"
            data-testid="city-back-home"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Sellerie Académie
          </Link>
          <p className="text-xs font-mono uppercase tracking-[0.35em] text-[#C9B896] mb-4">
            <MapPin className="inline w-3 h-3 mr-1.5" />
            SEO LOCAL · {page.region}
          </p>
          <h1 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl text-[#FAF7F0]">
            <span className="text-[#FAF7F0]">Formation sellier</span> <span className="italic text-[#C9B896]">depuis {page.city_name}.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-white/75 max-w-2xl font-light leading-relaxed">
            À <strong className="text-white">{page.distance_km}&nbsp;km</strong> de notre atelier d'Istres — la Sellerie Académie
            forme les selliers-garnisseurs de demain. Certifié Qualiopi, finançable France Travail / OPCO.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={L('/formations-sur-place')}>
              <Button className="bg-[#C9B896] text-[#0F1623] hover:bg-[#D9C8A8] rounded-full px-6 h-11" data-testid="city-cta-formations">
                Voir les formations
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to={L('/contact')}>
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 h-11 bg-transparent" data-testid="city-cta-contact">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Intro + body — admin-editable HTML */}
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
        <div
          className="prose prose-lg max-w-none text-[#1F2A4A] [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-light [&_h2]:text-[#1F2A4A] [&_h2]:mt-10 [&_h2]:mb-4 [&_p]:leading-relaxed [&_p]:text-[#1F2A4A]/85 [&_strong]:text-[#1F2A4A] [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1.5 [&_li]:text-[#1F2A4A]/85"
          data-testid="city-intro-html"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.intro_html) }}
        />
        {page.body_html && (
          <div
            className="prose prose-lg max-w-none mt-8 text-[#1F2A4A] [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-light [&_h2]:text-[#1F2A4A] [&_h2]:mt-10 [&_h2]:mb-4 [&_p]:leading-relaxed [&_p]:text-[#1F2A4A]/85 [&_strong]:text-[#1F2A4A] [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1.5 [&_li]:text-[#1F2A4A]/85"
            data-testid="city-body-html"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.body_html) }}
          />
        )}

        {/* Trust strip */}
        <div className="mt-12 pt-10 border-t border-[#1F2A4A]/10 grid grid-cols-2 md:grid-cols-3 gap-5">
          {[
            { Icon: ShieldCheck, label: 'Qualiopi', sub: 'Certifié' },
            { Icon: Clock, label: '3 à 23 jours', sub: 'Formats compacts' },
            { Icon: Award, label: '7 spécialités', sub: 'auto · moto · marine…' },
          ].map((it) => (
            <div key={it.label} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#C9B896]/15 flex items-center justify-center shrink-0">
                <it.Icon className="w-4 h-4 text-[#C9B896]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#1F2A4A]">{it.label}</p>
                <p className="text-[11px] text-[#1F2A4A]/60">{it.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 p-7 rounded-2xl bg-[#1F2A4A] text-white text-center">
          <h2 className="font-serif italic text-2xl md:text-3xl text-[#C9B896] mb-2">
            Prêt à commencer ?
          </h2>
          <p className="text-sm text-white/75 mb-5">
            Notre prochaine session démarre bientôt. Place est limitée.
          </p>
          <Link to={L('/inscription')}>
            <Button className="bg-[#C9B896] text-[#0F1623] hover:bg-[#D9C8A8] rounded-full px-7 h-11" data-testid="city-cta-signup">
              Candidater
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CityLandingPage;
